/** @jsxImportSource @emotion/react */
import { useLazyQuery, useMutation } from '@apollo/client'
import Typography from '@mui/material/Typography'
import { FC, useCallback, useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { findAllStrategyGroup_findAllStrategyGroup_items as Group } from '__generatedTypes__/findAllStrategyGroup'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import Badge from 'components/Badge'
import DeleteModal from 'components/Modals/DeleteModal'
import Table, { TableProps } from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import Loader from 'components/Loader'
import TabsNavigation from 'components/TabsNavigation'
import { TabsItem } from 'types/tabsItem'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { BULK_REMOVE_STRATEGY, BULK_UPDATE_STRATEGY, FIND_ALL_STRATEGY } from 'queries/strategies'
import {
  findAllStrategy,
  findAllStrategyVariables,
  findAllStrategy_findAllStrategy_items as Strategy,
} from '__generatedTypes__/findAllStrategy'
import { WEB_APP_ORIGIN } from 'pagesNew/Users'
import * as commonClasses from 'components/shared/GroupCards/commonStyles'
import * as classes from 'pagesNew/FlashCards/FlashCardsTabs/styles'
import { SELECTED_SUBJECT_ID_PARAM } from 'pagesNew/SuccessStory/Create/GroupSelect'
import { bulkUpdateStrategy, bulkUpdateStrategyVariables } from '__generatedTypes__/bulkUpdateStrategy'
import { bulkRemoveStrategy, bulkRemoveStrategyVariables } from '__generatedTypes__/bulkRemoveStrategy'
import { toast } from 'utils/toast'
import * as strategiesClasses from '../StrategiesGroups/styles'
import { QUERY_PARAM as SUBJECT_QUERY_PARAM } from '../../../List'

interface Props {
  groups: Group[]
  initialSelected: Group
  selectedSubjectId: string
}

export const CARD_GROUP_QUERY_PARAM = 'card-group'

export const GroupCards: FC<Props> = ({ groups, selectedSubjectId, initialSelected }) => {
  const { navigate, getLink } = useLink()
  const { platform } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [getStrategies, { loading }] = useLazyQuery<findAllStrategy, findAllStrategyVariables>(FIND_ALL_STRATEGY)
  const [strategyCards, setStrategyCards] = useState<Strategy[]>([])
  const [selectedGroup, setSelectedGroup] = useState<Group>(initialSelected)
  const [isBulkDeleteModalOpen, setBulkDeleteModalOpen] = useState<boolean>(false)
  const [strategyCardsIdsToDelete, setStrategyCardsIdsToDelete] = useState<Strategy[]>([])
  const [bulkUpdateStrategies] = useMutation<bulkUpdateStrategy, bulkUpdateStrategyVariables>(BULK_UPDATE_STRATEGY)
  const [bulkRemoveStrategy] = useMutation<bulkRemoveStrategy, bulkRemoveStrategyVariables>(BULK_REMOVE_STRATEGY)

  const initializeStrategies = async () => {
    if (selectedGroup) {
      const { data: strategyCardsResponse } = await getStrategies({
        variables: {
          query: {
            take: TAKE_ALL_ITEMS_COUNT,
            where: {
              strategyGroup: {
                id: selectedGroup.id,
              },
            },
          },
        },
        fetchPolicy: 'cache-and-network',
      })

      setStrategyCards(strategyCardsResponse?.findAllStrategy.items || [])
    }
  }

  useEffect(() => {
    setStrategyCardsIdsToDelete([])
    initializeStrategies()
  }, [selectedGroup])

  useEffect(() => {
    if (selectedGroup && !groups.find((group) => group.id === selectedGroup.id)) {
      setSelectedGroup(groups[0])
    }
  }, [groups])

  useEffect(() => {
    setStrategyCards([])
  }, [selectedSubjectId])

  const handleBulkDelete = useCallback(async () => {
    const ids = strategyCardsIdsToDelete.map((group) => ({ id: group.id }))

    try {
      await bulkRemoveStrategy({ variables: { ids } })
      setStrategyCardsIdsToDelete([])
      setBulkDeleteModalOpen(false)

      initializeStrategies()
      toast.success('Cards deleted successfully')
    } catch (error) {
      toast.error('Failed to delete cards')
    }
  }, [strategyCardsIdsToDelete])

  const handleChangeGroup = ({ id }: TabsItem) => {
    const selectedGroup = groups.find(({ id: groupId }) => groupId === id)

    if (selectedGroup) {
      setSelectedGroup(selectedGroup)

      searchParams.set(CARD_GROUP_QUERY_PARAM, selectedGroup.id)
      setSearchParams(searchParams, { replace: true })
    }
  }

  const handleSort = useCallback(async (items: Strategy[]) => {
    try {
      if (items.length > 0) {
        setStrategyCards(items)

        await bulkUpdateStrategies({
          variables: {
            bulkUpdateStrategyInput: items.map(({ id }, order) => ({ id, order })),
          },
        })

        toast.success('Cards order updated successfully')
      }
    } catch (error) {
      toast.error('Failed to update cards order')
    }
  }, [])

  const subject = searchParams.get(SUBJECT_QUERY_PARAM)
  const linkParams = new URLSearchParams({
    [SUBJECT_QUERY_PARAM]: String(subject),
    [SELECTED_SUBJECT_ID_PARAM]: selectedGroup.subject.id,
    [CARD_GROUP_QUERY_PARAM]: selectedGroup.id,
  })

  const StrategyCardsColumn: TableProps<Strategy>['columns'] = [
    {
      title: 'Title',
      renderCell: (strategy) => (
        <Typography css={commonClasses.longText} variant="body2" data-testid="card-title">
          {strategy.title}
        </Typography>
      ),
      flex: '0 1 300px',
    },
    {
      title: 'Group',
      renderCell: () => <Badge text={selectedGroup.title} color={selectedGroup.color} css={classes.badge} />,
      flex: '0 1 250px',
    },
    {
      title: 'URL',
      renderCell: (strategy) => (
        <a
          href={`${WEB_APP_ORIGIN}/${platform}/${subject}/${strategy.slug}`}
          target="_blank"
          css={classes.link}
          rel="noreferrer"
        >
          {WEB_APP_ORIGIN}/{platform}/{subject}/{strategy.slug}
        </a>
      ),
      flex: '0 1 40%',
    },
    {
      renderCell: (strategy) => (
        <Link to={getLink(cmsRoutes.EDIT_PAT_STRATEGY, { patStrategyId: strategy.id }, linkParams)}>
          <EditIcon css={commonClasses.editIcon} data-testid="edit-card-btn" />
        </Link>
      ),
      flex: '0 1 29px',
    },
  ]

  const renderContent = () => {
    if (!groups?.length) {
      return null
    }

    return (
      <>
        <div css={classes.tabsBlock}>
          <TabsNavigation tabs={groups} value={selectedGroup} onChange={handleChangeGroup} css={classes.tabsBlock} />
        </div>
        {loading ? (
          <Loader />
        ) : (
          <Table
            css={strategiesClasses.table}
            columns={StrategyCardsColumn}
            items={strategyCards}
            isSelectable
            selectedItems={strategyCardsIdsToDelete}
            onSelect={setStrategyCardsIdsToDelete}
            onSort={handleSort}
            isSortable
          />
        )}
      </>
    )
  }

  return (
    <div>
      <Typography css={classes.title} variant="title3" component="p">
        Cards
      </Typography>

      {renderContent()}

      {selectedGroup && (
        <TableButtons
          itemName="Cards"
          areItemsToDelete={strategyCardsIdsToDelete.length !== 0}
          onAddItem={() => navigate(cmsRoutes.CREATE_PAT_STRATEGY, {}, { searchParams: linkParams })}
          onCancel={() => setStrategyCardsIdsToDelete([])}
          onBatchDelete={() => setBulkDeleteModalOpen(true)}
        />
      )}
      <DeleteModal
        open={isBulkDeleteModalOpen}
        count={strategyCardsIdsToDelete.length}
        onClose={() => setBulkDeleteModalOpen(false)}
        onConfirm={handleBulkDelete}
      />
    </div>
  )
}
